import React, {
  useCallback,
  useContext,
  useRef,
  useState,
  useEffect,
} from 'react';
import ExclamationIcon from 'icons/Exclamation.icon';
import classNames from 'classnames';
import { Field, useField, useFormState } from 'react-final-form';
import InputField from 'components/InputField';
import validations from 'validations';
import { useTranslation } from 'react-i18next';
import utils, { sentryError } from 'utils';
import api from 'api';
import ListingContext from '../../providers/Listing/Listing.context';
import PatchListingContext from '../../providers/PatchListing/PatchListing.context';
import ListingContextEdit from '../../subrouters/EditListingSubrouter/providers/Listing/Listing.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

import './ListingSlugField.styles.scss';
import './ListingSlugField.styles.responsive.scss';

type ListingSlugFieldProps = {
  className?: string;
  ref?: (element: any) => void;
};

export const SLUG_FIELD_NAME = 'slugName';
const LISTING_TITLE_FIELD_NAME = 'listingTitle';

const ListingSlugField: React.FC<ListingSlugFieldProps> = (props) => {
  const { className } = props;

  const [doesExist, setDoesExist] = useState(false);

  const isFirstSlug = useRef(true);

  const { t } = useTranslation();

  const { isSuperAdmin } = useContext(CurrentUserContext);

  const { listing: listingCreate } = useContext(ListingContext);
  const { listing: listingEdit } = useContext(ListingContextEdit);
  const { patchListingValue } = useContext(PatchListingContext);

  const { _id: listingId } = listingCreate || listingEdit;

  const {
    input: { value: listingTitle },
    meta: { touched: titleTouched },
  } = useField(LISTING_TITLE_FIELD_NAME);

  const {
    input: { onChange: changeSlug },
  } = useField(SLUG_FIELD_NAME);

  const { values } = useFormState();
  const currentSlug = values[SLUG_FIELD_NAME];

  const checkAndUpdateSlug = useCallback(async () => {
    if (!currentSlug) return null;

    try {
      const {
        data: { exist, slugName },
      } = await api.listings.doesSlugExist(currentSlug, listingId);

      if (exist) {
        changeSlug(slugName);
        setDoesExist(true);
        patchListingValue({ [SLUG_FIELD_NAME]: slugName });
      }
    } catch (e) {
      sentryError(e);
    }
  }, [currentSlug, listingId, changeSlug, patchListingValue]);

  // setting initial value
  useEffect(() => {
    if (!isSuperAdmin || currentSlug || !titleTouched || !listingTitle) return;
    const newSlug = utils.createSlug(listingTitle);
    changeSlug(newSlug);

    if (newSlug) patchListingValue({ [SLUG_FIELD_NAME]: newSlug });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeSlug, listingTitle, titleTouched]);

  useEffect(() => {
    if (!isSuperAdmin || !currentSlug || !isFirstSlug.current) return;
    checkAndUpdateSlug();
    isFirstSlug.current = false;
  }, [checkAndUpdateSlug, currentSlug, isSuperAdmin]);

  const classes = classNames('listing-slug-field', className);

  return (
    isSuperAdmin && (
      <>
        {doesExist && (
          <p className="already-exist-banner">
            <ExclamationIcon />
            <span>{t('slugAlreadyExistBanner')}</span>
          </p>
        )}
        <Field
          label={t('listingTitleSlug')}
          name={SLUG_FIELD_NAME}
          placeholder={t('lsitingUrlPlaceholder')}
          className={classes}
          component={InputField}
          validate={validations.required()}
          format={(value: string) => {
            if (!value) return '';
            return utils.createSlug(value);
          }}
          infoPopupContent={t('listingTitleSlugInfo')}
          formatOnBlur
          required
          customOnBlur={(
            e: React.FocusEvent<HTMLInputElement>,
            isValid: boolean,
          ) => {
            if (!isValid) return;

            // formatOnBlur has not reached the DOM yet, so the event still
            // carries the raw text. Slug it the same way `format` does, or the
            // unhyphenated value is what gets sent.
            patchListingValue({
              [SLUG_FIELD_NAME]: utils.createSlug(e.target.value),
            });
          }}
        />
      </>
    )
  );
};

export default ListingSlugField;
